import { ORDER_STATUSES, orderStatusSchema, type OrderAdminSummary } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  Cell,
  formatDateTime,
  LoadMore,
  Loading,
  PageHeader,
  SelectField,
  Table,
  TextField,
  useListFilters,
} from '@avrahami/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SyntheticEvent } from 'react';
import { NO_ORDER_FILTERS, orderListQuery, useStoreTimeZone } from '../api/tenant';
import { OrderStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';
import { formatMoney } from '../lib/money';

/**
 * A table of quote requests (also used on the customer card). The amount is what the requested
 * lines come to at the store's list prices: the shopper never saw it.
 */
export function QuoteRequestsTable({ orders }: { orders: readonly OrderAdminSummary[] }) {
  const timeZone = useStoreTimeZone();
  return (
    <Table
      head={[
        he.orders.number,
        he.orders.date,
        he.orders.customer,
        he.orders.items,
        he.orders.status,
        he.orders.total,
      ]}
    >
      {orders.map((order) => (
        <tr key={order.id} data-testid="order-row" data-number={order.number}>
          <Cell>
            <Link
              to="/quote-requests/$orderId"
              params={{ orderId: order.id }}
              className="font-medium text-brand-700 hover:underline"
            >
              {order.number}
            </Link>
          </Cell>
          <Cell className="whitespace-nowrap">{formatDateTime(order.createdAt, timeZone)}</Cell>
          <Cell>{order.contactName}</Cell>
          <Cell>{order.itemCount}</Cell>
          <Cell>
            <OrderStatusBadge status={order.status} />
          </Cell>
          <Cell className="whitespace-nowrap">{formatMoney(order.totalMinor)}</Cell>
        </tr>
      ))}
    </Table>
  );
}

export function QuoteRequestsPage() {
  const {
    draft,
    filters,
    change: set,
    apply,
    reset,
  } = useListFilters('quote-requests', NO_ORDER_FILTERS);
  const orders = useInfiniteQuery(orderListQuery(filters));
  const items = orders.data?.pages.flatMap((page) => page.items) ?? [];

  const onFilter = (event: SyntheticEvent) => {
    event.preventDefault();
    apply();
  };

  return (
    <>
      <PageHeader title={he.orders.title} />
      <Card>
        <form
          onSubmit={onFilter}
          className="mb-4 flex flex-wrap items-end gap-3"
          aria-label={he.orders.filters}
        >
          <SelectField
            label={he.orders.status}
            name="status"
            value={draft.status}
            onChange={(event) => {
              const value = event.target.value;
              set({ status: value === '' ? '' : orderStatusSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {he.orderStatus[status]}
              </option>
            ))}
          </SelectField>
          <TextField
            label={he.orders.search}
            name="q"
            type="search"
            className="w-72"
            maxLength={100}
            value={draft.q}
            onChange={(event) => {
              set({ q: event.target.value });
            }}
          />
          <TextField
            label={he.orders.from}
            name="from"
            type="date"
            value={draft.from}
            onChange={(event) => {
              set({ from: event.target.value });
            }}
          />
          <TextField
            label={he.orders.to}
            name="to"
            type="date"
            value={draft.to}
            onChange={(event) => {
              set({ to: event.target.value });
            }}
          />
          <Button type="submit" tone="secondary">
            {he.orders.apply}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              reset();
            }}
          >
            {he.common.clear}
          </Button>
        </form>
        {orders.isPending ? <Loading /> : null}
        {orders.isError ? <Alert>{errorMessage(orders.error)}</Alert> : null}
        {orders.isSuccess && items.length === 0 ? (
          <p className="text-sm text-slate-500">{he.orders.empty}</p>
        ) : null}
        {items.length > 0 ? <QuoteRequestsTable orders={items} /> : null}
        <LoadMore
          hasMore={orders.hasNextPage}
          busy={orders.isFetchingNextPage}
          onMore={() => {
            void orders.fetchNextPage();
          }}
        />
      </Card>
    </>
  );
}
